"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, LogOut, Search } from "lucide-react";
import { toggleGlobalSearch } from "@/stores/globalSearchStore";
import NotificationBell from "@/components/notifications/NotificationBell";
import { DegradedModeBadge } from "@/components/notifications/DegradedModeBadge";
import { ThemeToggle } from "@/components/layout/ThemeToggle";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCallContext } from "@/contexts/CallContext";
import { mostraOChip } from "@/lib/chamadas/chip";
import { StatusDropdown } from "@/components/layout/StatusDropdown";
import { PresenceDot } from "@/components/chat/PresenceDot";
import { useMinhaFoto } from "@/hooks/useMinhaFoto";
import { useSignedImageUrl } from "@/hooks/useSignedImageUrl";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();
  // Fora do `CallProvider` o valor é inerte (`ligacao: null`): sem faixa e sem erro.
  const { ligacao } = useCallContext();
  const [isMobile, setIsMobile] = useState(false);

  // Detectar se é mobile com melhor performance
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();

    // Otimizar evento de resize
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, []);

  const handleLogout = () => {
    authLogout();
    navigate("/login");
  };

  const userName =
    user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : (user?.email ?? "Usuário");

  // Achado 5 (28/09/2026) — mesmo padrão de `Home.tsx`: `<img>` PRÓPRIO
  // dentro do `Avatar`, não `AvatarImage` do Radix (ele só monta o `<img>`
  // depois de carregar, e o `onError` da reassinatura nunca dispararia — ver
  // `useSignedImageUrl.ts`). A troca de foto em `/perfil` invalida
  // `["users", userId, "foto"]`, que é a MESMA chave de `useMinhaFoto` — o
  // topo atualiza junto, sem ação extra aqui.
  const minhaFoto = useMinhaFoto(user?.user_id);
  const {
    displayUrl: fotoExibida,
    failed: fotoFalhou,
    onError: onFotoError,
    onLoad: onFotoLoad,
  } = useSignedImageUrl(minhaFoto);

  return (
    <header className="glass glass-header shadow-sm border-b">
      <div className={`${isMobile ? "px-3 py-2" : "px-6 py-4"}`}>
        <div className="flex items-center gap-3">
          {/* Busca global ÚNICA (Story E3.2.1 unificada): campo no desktop /
              ícone no mobile, ambos abrem a command palette ⌘K. A antiga
              GlobalSearchBox central foi removida — um só ponto de entrada de
              busca no topo (a cobertura de colaborador/loja/equipamento agora
              é alcançada pelo atalho "Busca avançada" dentro da palette). */}
          <div className="flex flex-1 min-w-0 items-center">
            {!isMobile && (
              <button
                type="button"
                onClick={toggleGlobalSearch}
                aria-label="Buscar (atalho Ctrl/Cmd K)"
                data-testid="global-search-trigger"
                className="flex h-10 w-full max-w-md min-w-[44px] items-center gap-2 rounded-md border border-input bg-background px-3 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
                <span className="flex-1 text-left">Buscar...</span>
                <kbd className="rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none">
                  ⌘K
                </kbd>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {isMobile && (
              <Button
                variant="ghost"
                onClick={toggleGlobalSearch}
                aria-label="Buscar (atalho Ctrl/Cmd K)"
                data-testid="global-search-trigger-mobile"
                className="h-11 w-11 min-h-[44px] min-w-[44px]"
              >
                <Search className="h-5 w-5" />
              </Button>
            )}

            {/* Toggle de tema */}
            <ThemeToggle />

            {/* Story E6.3.2: dropdown de status manual (online/away/busy). Esconder em mobile (espaço). */}
            {!isMobile && <StatusDropdown />}
            {/* Badge de modo degradado (Story E1.5.3) — só aparece se transport caiu pra SSE */}
            {!isMobile && <DegradedModeBadge />}

            {/* Sino de notificações (Story 2.5) — também no celular desde a parte D1:
                entre o tema e o avatar, como no desktop; lá abre a folha de baixo. */}
            <NotificationBell compacto={isMobile} />

            {/* Menu do usuário */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`relative ${isMobile ? "h-8 w-8" : "h-9 w-9"} rounded-full min-h-[32px] min-w-[32px]`}
                >
                  <Avatar className={`${isMobile ? "h-8 w-8" : "h-9 w-9"}`}>
                    {fotoExibida && !fotoFalhou ? (
                      <img
                        src={fotoExibida}
                        alt={`Foto de ${userName}`}
                        onError={onFotoError}
                        onLoad={onFotoLoad}
                        className="aspect-square h-full w-full object-cover"
                        data-testid="foto-do-usuario"
                      />
                    ) : (
                      <AvatarFallback className={`${isMobile ? "text-xs" : "text-sm"}`}>
                        {user?.first_name && user?.last_name
                          ? `${user.first_name[0]}${user.last_name[0]}`
                          : (user?.email?.[0]?.toUpperCase() ?? "U")}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  {/* Story E6.3.2: dot overlay no avatar self. Em mobile sem StatusDropdown,
                      é a única indicação visual do meu status. */}
                  {user?.user_id && (
                    <PresenceDot
                      userId={user.user_id}
                      size="sm"
                      className="absolute -bottom-0.5 -right-0.5"
                    />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className={`${isMobile ? "w-48" : "w-56"}`}
                align="end"
                forceMount
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium leading-none`}>
                      {user?.first_name && user?.last_name
                        ? `${user.first_name} ${user.last_name}`
                        : (user?.email ?? "Usuário")}
                    </p>
                    <p
                      className={`${isMobile ? "text-xs" : "text-xs"} leading-none text-muted-foreground truncate`}
                    >
                      {user?.email ?? ""}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/perfil")} className="min-h-[32px]">
                  <User className="mr-2 h-4 w-4" />
                  <span className={isMobile ? "text-xs" : "text-sm"}>Perfil</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="min-h-[32px]">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span className={isMobile ? "text-xs" : "text-sm"}>Sair</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      {/* No celular o chip da ligação (`JanelaDaLigacao`, `fixed`, `z-[35]`, `top-16`) MORA aqui: esta faixa de 60 px
          (`52px` do chip + `0.5rem` de folga) o reserva dentro do cabeçalho e empurra a página inteira para baixo
          enquanto a ligação dura, em vez de o chip flutuar sobre o ⋮ da conversa e sobre o título das páginas
          (desenho das sobras §11). `md:hidden`, não `isMobile &&`: o chip troca de roupa por CSS (`md:`) e a faixa tem
          de trocar no mesmo pixel. `mostraOChip` é a regra da própria janelinha: nunca faixa sem chip, nem chip sem
          faixa. */}
      {mostraOChip(ligacao) && (
        <div
          aria-hidden="true"
          data-testid="espaco-da-ligacao"
          className="h-[calc(52px+0.5rem)] md:hidden"
        />
      )}
    </header>
  );
};

export default Header;
